import { useState, useRef, useTransition, useEffect } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import VariablePickerPlugin from "./plugins/VariablePickerPlugin";
import styles from "./index.less";
import VariablePlugin from "./plugins/VariablePlugin";
import { VariableNode } from "./plugins/VariablePlugin/node";
import { EditorState, LexicalEditor } from "lexical";
import OnBlurPlugin from "./plugins/OnBlurPlugin";
import FocusPlugin from "./plugins/FocusPlugin";
import { textToEditorState } from "./utils";
import { nanoid } from "nanoid";
import { ReactComponent as Resizer } from "../../../../assets/resizer.svg";
import { EditorVariablePickerType } from "../types";
import { SingleLinePlugin } from "./plugins/SingleLinePlugin";
import { ReactComponent as FullscreenIcon } from "../../../../assets/fullscreen.svg";

export default function Editor({
  h = 200,
  showResize = true,
  showOpenModal = true,
  onOpenModal,
  variables,
  onChange,
  onBlur,
  value,
  placeholder = "",
  border = true,
  singleLine = false,
}: {
  h?: number;
  showResize?: boolean;
  showOpenModal?: boolean;
  onOpenModal?: () => void;
  variables: EditorVariablePickerType[];
  onChange?: (editorState: EditorState) => void;
  onBlur?: (editor: LexicalEditor) => void;
  value?: string;
  currentValue?: string;
  placeholder?: string;
  border?: boolean;
  singleLine?: boolean;
}) {
  const [key, setKey] = useState(() => nanoid());
  const [, startSts] = useTransition();
  const [height, setHeight] = useState(h);
  const [focus, setFocus] = useState(false);
  const initialConfig = {
    namespace: "promptEditor",
    nodes: [VariableNode],
    editorState: textToEditorState(value),
    onError: (error: Error) => {
      throw error;
    },
  };

  const initialY = useRef(0);
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    console.log(e);
    initialY.current = e.clientY;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - initialY.current;
      setHeight((prevHeight) =>
        prevHeight + deltaY < h * 0.5 ? h * 0.5 : prevHeight + deltaY
      );
      initialY.current = e.clientY;
    };

    const handleMouseUp = () => {
      console.log("mouseup");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    if (focus) return;
    setKey(nanoid());
  }, [value, variables.length]);

  return (
    <div
      style={{
        height,
        width: "100%",
        border: !border ? "none" : "",
        flex: 1,
      }}
      className={`${styles.container} nowheel ${
        singleLine && styles["single-line-container"]
      }`}
    >
      <LexicalComposer initialConfig={initialConfig} key={key}>
        <PlainTextPlugin
          contentEditable={
            <ContentEditable className={styles["content-editable"]} />
          }
          placeholder={
            <div className={styles.placeholder}>
              <div style={{ height: "100%" }}>{placeholder}</div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <FocusPlugin focus={focus} setFocus={setFocus} />
        <OnChangePlugin
          onChange={(e) => {
            startSts(() => {
              onChange?.(e);
            });
          }}
        />
        <VariablePickerPlugin variables={variables} />
        <VariablePlugin variables={variables} />
        {singleLine && <SingleLinePlugin />}
        <OnBlurPlugin onBlur={onBlur} />
      </LexicalComposer>
      {showResize && (
        <Resizer onMouseDown={handleMouseDown} className={styles.resize} />
      )}
      {showOpenModal && (
        <FullscreenIcon
          onMouseDown={onOpenModal}
          className={styles.fullscreen}
        />
      )}
    </div>
  );
}
